import { useEffect, useRef } from 'react'
import { Plane, ArrowRight, Clock, Shield, Star } from 'lucide-react'
import PageHeader from '../components/PageHeader'
import Seo from '../components/Seo'

export default function Flights() {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    // Handle iframe resize messages from Travelstart
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://www.travelstart.co.za') return
      
      if (iframeRef.current && event.data && typeof event.data === 'object') {
        if (event.data.type === 'resize' && event.data.height) {
          iframeRef.current.style.height = `${event.data.height}px`
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])


  return (
    <div className="bg-white">
      <Seo
        title="Flight Bookings - Memento Travel Co."
        description="Search and book flights worldwide with expert travel consultants. Best prices guaranteed on domestic and international flights."
        canonicalUrl={typeof window !== 'undefined' ? window.location.href : 'https://www.mementotravel.co/flights'}
        pageType="flights"
      />
      
      <PageHeader
        title="Flight Bookings"
        description="Find the best flight deals worldwide with our expert booking service"
      />

      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          {/* Travelstart Flight Search Iframe */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-16">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-serif text-gray-900 mb-4">
                Search for <span style={{color: '#006D77'}}>Flights</span>
              </h2>
              <p className="text-gray-600">Let our experts help you find the best flight deals</p>
            </div>
            
            <iframe
              ref={iframeRef}
              id="travelstartIframe-39daee2d-b6c4-4404-bbbc-efabeb5045db"
              frameBorder="0"
              scrolling="auto"
              style={{
                margin: 0,
                padding: 0,
                border: 0,
                height: '800px',
                width: '100%',
                backgroundColor: '#FAFAFA'
              }}
              src="https://www.travelstart.co.za/?search=false&show_banners=false&log=false&affId=5242698&language=&affCampaign=&cpy_source=tszaweb&utm_source=affiliate&utm_medium=5242698&isiframe=true&landing_page=false&iframeVersion=11&host=https://www.mementotravelco.co.za/flights"
              title="Travelstart Flight Search"
            />
          </div>


          {/* Why Book With Us */}
          <div className="bg-[#F7F7E9] rounded-3xl p-8 md:p-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4">
                Why Book Flights with <span style={{color: '#006D77'}}>Memento Travel</span>?
              </h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                Our flight booking experts have access to exclusive deals and can help you find the best routes and prices.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div 
                  className="flex h-16 w-16 items-center justify-center rounded-full mx-auto mb-4"
                  style={{ background: '#006D77' }}
                >
                  <Star className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Best Prices</h3>
                <p className="text-sm text-gray-600">Access to exclusive deals and competitive pricing</p>
              </div>

              <div className="text-center">
                <div 
                  className="flex h-16 w-16 items-center justify-center rounded-full mx-auto mb-4"
                  style={{ background: '#006D77' }}
                >
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">24/7 Support</h3>
                <p className="text-sm text-gray-600">Round-the-clock assistance for your travel needs</p>
              </div>

              <div className="text-center">
                <div 
                  className="flex h-16 w-16 items-center justify-center rounded-full mx-auto mb-4"
                  style={{ background: '#006D77' }}
                >
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure Booking</h3>
                <p className="text-sm text-gray-600">Safe and protected flight reservations</p>
              </div>

              <div className="text-center">
                <div 
                  className="flex h-16 w-16 items-center justify-center rounded-full mx-auto mb-4"
                  style={{ background: '#006D77' }}
                >
                  <Plane className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Expert Advice</h3>
                <p className="text-sm text-gray-600">Professional guidance on routes and airlines</p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-20 text-center">
            <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4">
              Need Help Finding the Perfect Flight?
            </h2>
            <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
              Our travel experts are here to help you find the best flight deals and routes. 
              Contact us for personalized assistance.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="/#contact"
                className="inline-flex items-center gap-2 text-white px-8 py-4 rounded-lg shadow-lg hover:opacity-90 transition-all duration-200 font-medium"
                style={{background: '#006D77'}}
                onClick={(e) => {
                  e.preventDefault()
                  setTimeout(() => {
                    const element = document.getElementById('contact');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }, 100);
                }}
              >
                Contact Us <ArrowRight className="h-5 w-5" />
              </a>
              <a 
                href="/services"
                className="inline-flex items-center gap-2 text-gray-700 border-2 border-gray-300 px-8 py-4 rounded-lg hover:border-gray-400 transition-colors font-medium"
              >
                View All Services <ArrowRight className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
